import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";
import {
  validateNotifyContactMessage,
  buildNotifyContactSuggestedMessage,
  buildNotifyTaskContactPayload,
  classifyNotifyContactError,
  isTrustedContactName
} from "../../pages/Tasks/taskUtils";

const useStyles = makeStyles(theme => ({
  notice: {
    marginTop: theme.spacing(1)
  },
  charCount: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(0.5)
  }
}));

const MESSAGE_MAX_LENGTH = 4096;

// Modal de confirmação para "Avisar contato" — mensagem sugerida (editável),
// nunca enviada automaticamente. Cada abertura é um MOUNT novo (o pai só
// renderiza <NotifyTaskContactModal /> condicionalmente, mesmo padrão de
// TaskCompleteDialog/TaskCancelDialog/TaskTransferDialog) — por isso o
// useState inicial já resolve sozinho "gerar mensagem nova/limpar erro
// anterior ao abrir para outra tarefa", sem precisar de efeito de reset.
const NotifyTaskContactModal = ({ open, onClose, task, onSuccess }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [message, setMessage] = useState(() =>
    buildNotifyContactSuggestedMessage({
      contact: task?.contact,
      taskTitle: task?.title,
      // i18next depende do próprio contexto interno (`this`). Passar
      // `i18n.t` diretamente desassocia esse contexto e derruba a
      // renderização do diálogo com uma tela branca.
      translate: (key, options) => i18n.t(key, options)
    })
  );
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState(null);

  // Trava síncrona: dois cliques podem acontecer antes do React aplicar o
  // setSaving(true) do primeiro (setState é assíncrono/em lote) — só o
  // useRef garante, de forma síncrona, que apenas uma chamada HTTP saia
  // daqui, mesmo com cliques em sequência muito rápida.
  const submittingRef = useRef(false);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (submittingRef.current) return;

    const error = validateNotifyContactMessage(message);
    if (error) {
      setFieldError(error);
      return;
    }

    submittingRef.current = true;
    setSaving(true);
    setFieldError(null);

    try {
      const payload = buildNotifyTaskContactPayload({ message });
      const { data } = await api.post(`/tasks/${task.id}/notify-contact`, payload);

      toast.success(i18n.t("tasks.details.actions.notifyContact.success"));
      if (typeof onSuccess === "function") onSuccess(data);
      onClose();
    } catch (err) {
      const classification = classifyNotifyContactError(err);

      if (classification === "alreadyNotified") {
        // Nunca reenvia. Busca o estado real e só aplica se a consulta
        // funcionar — nunca mostra "enviado por você" (não foi).
        try {
          const { data } = await api.get(`/tasks/${task.id}`);
          if (typeof onSuccess === "function") onSuccess(data);
          toast.info(i18n.t("tasks.details.actions.notifyContact.alreadyNotified"));
        } catch (refreshErr) {
          toast.error(i18n.t("tasks.details.actions.notifyContact.alreadyNotifiedRefreshFailed"));
        }
        onClose();
      } else if (classification === "phoneUnconfirmed") {
        toast.error(i18n.t("tasks.details.actions.notifyContact.phoneUnconfirmedError"));
        // Fecha para impedir novas tentativas a partir de uma tela que ainda
        // estava com os dados antigos do contato.
        onClose();
      } else if (classification === "ambiguous") {
        // Pode ter sido entregue mesmo sem confirmação — nunca incentivar
        // reenvio imediato. Fecha o modal (o botão "Confirmar envio" não
        // deve continuar disponível) e orienta conferência manual.
        toast.error(i18n.t("tasks.details.actions.notifyContact.ambiguousError"));
        onClose();
      } else {
        toastError(err);
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const contactName = isTrustedContactName(task?.contact?.name, {
    number: task?.contact?.number,
    lid: task?.contact?.lid
  })
    ? task.contact.name.trim()
    : i18n.t("tasks.table.none");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="notify-task-contact-dialog-title"
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
    >
      <DialogTitle id="notify-task-contact-dialog-title">
        {i18n.t("tasks.details.actions.notifyContact.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("tasks.details.actions.notifyContact.contactLabel")}
        </Typography>
        <Typography variant="body2" gutterBottom>
          {contactName}
        </Typography>

        <Typography variant="caption" color="textSecondary">
          {i18n.t("tasks.details.actions.notifyContact.taskLabel")}
        </Typography>
        <Typography variant="body2" gutterBottom style={{ overflowWrap: "break-word" }}>
          {task?.title}
        </Typography>

        <TextField
          label={i18n.t("tasks.details.actions.notifyContact.messageLabel")}
          variant="outlined"
          size="small"
          fullWidth
          required
          multiline
          minRows={6}
          value={message}
          onChange={e => {
            setMessage(e.target.value);
            setFieldError(null);
          }}
          disabled={saving}
          error={Boolean(fieldError)}
          helperText={
            fieldError
              ? i18n.t(`tasks.details.actions.notifyContact.errors.${fieldError}`)
              : ""
          }
        />
        <Box className={classes.charCount}>
          <Typography
            variant="caption"
            color={message.length > MESSAGE_MAX_LENGTH ? "error" : "textSecondary"}
          >
            {message.length}/{MESSAGE_MAX_LENGTH}
          </Typography>
        </Box>

        <Typography variant="body2" color="textSecondary" className={classes.notice}>
          {i18n.t("tasks.details.actions.notifyContact.pendingTicketNotice")}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving} variant="outlined">
          {i18n.t("tasks.details.actions.notifyContact.cancelButton")}
        </Button>
        <ButtonWithSpinner
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          loading={saving}
        >
          {i18n.t("tasks.details.actions.notifyContact.confirmButton")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default NotifyTaskContactModal;
